import { ICoreContext } from '@msdyn365-commerce/core';
import { Column, ColumnFilter, Table } from '@tanstack/react-table';
import React, { useCallback, useEffect, useState } from 'react';
import { ILxsBillingDocument } from '../actions/get-billing-documents.action';
import { IOrderHistoryTableData } from '../actions/order-history/get-order-history.action';
import { DateLocale, getDateFormat } from '../actions/user-preferences/get-localization.action';
import { MULTI_VALUE_SEPARATOR } from '../configuration';
import { ILxsBillingDocumentsResources } from '../modules/lxs-billing-documents/lxs-billing-documents.props.autogenerated';
import { IOrderHistoryResources } from '../modules/order-history';
import { ILocalizationState } from '../states/localization-state';
import { dateLocaleFormatter, isMobileView } from '../utilities';
import { updateUrlParams, UrlParamAction } from '../utilities/update-url-params';

type FilterType = 'date' | 'multi';

interface BaseFilterConfig {
    id: string;
    type: FilterType;
    legend?: string;
}

export interface DateFilterConfig extends BaseFilterConfig {
    type: 'date';
    columnId: string;
    fromParamName: string;
    toParamName: string;
}

export interface MultiFilterConfig extends BaseFilterConfig {
    type: 'multi';
    legend: string;
    columnId: string;
    paramName: string;
    mapKeyToText?: (key: string) => string;
    explicitKeys?: string[];
}

export type FilterConfig = DateFilterConfig | MultiFilterConfig;

interface GenericFilterBoxProps {
    table: Table<ILxsBillingDocument> | Table<IOrderHistoryTableData>;
    resources: ILxsBillingDocumentsResources | IOrderHistoryResources;
    context: ICoreContext;
    localizationState: ILocalizationState;
    columnFilters: ColumnFilter[];
    isFilterCollapsed?: boolean;
    isCollapsible: boolean;
    filters: FilterConfig[];
    setIsFilterCollapsed?: (fn: (collapsed: boolean) => boolean) => void;
    setColumnFilters: (filters: ColumnFilter[]) => void;
    setExpanded?: (_: object) => void;
}

const formatDateForInput = (dateString: string, localizationState: ILocalizationState) =>
    dateLocaleFormatter(localizationState).formatDate(dateString);

const DateFilter: React.FC<{
    filter: DateFilterConfig;
    table: Table<IOrderHistoryTableData> | Table<ILxsBillingDocument>;
    resources: ILxsBillingDocumentsResources | IOrderHistoryResources;
    localizationState: ILocalizationState;
    CLASS_NAME: string;
    context: ICoreContext;
}> = ({ filter, table, resources, localizationState, CLASS_NAME }) => {
    const column = table.getColumn(filter.columnId);

    const parseColumnValue = useCallback((value: [string, string]): [string, string] => {
        if (value === null) {
            return ['', ''];
        }
        if (Array.isArray(value)) {
            const parts = value.map(v => (v ?? '').trim());
            return [parts[0] ?? '', parts[1] ?? ''];
        }

        return ['', ''];
    }, []);

    const parseUrlForDates = useCallback((): [string, string] => {
        try {
            const url = new URL(window.location.href);
            const fromArr = url.searchParams
                .getAll(filter.fromParamName ?? 'fromDate')
                .map(s => s.trim())
                .filter(Boolean);
            const toArr = url.searchParams
                .getAll(filter.toParamName ?? 'toDate')
                .map(s => s.trim())
                .filter(Boolean);
            return [fromArr[0] ?? '', toArr[0] ?? ''];
        } catch {
            return ['', ''];
        }
    }, [filter.fromParamName, filter.toParamName]);

    const [selectedFromDate, setSelectedFromDate] = useState<string>('');
    const [selectedToDate, setSelectedToDate] = useState<string>('');

    useEffect(() => {
        const columnValue = column?.getFilterValue();

        const [colFrom, colTo] = parseColumnValue(columnValue as [string, string]);
        if (colFrom || colTo) {
            setSelectedFromDate(colFrom);
            setSelectedToDate(colTo);
            return;
        }
        const [urlFrom, urlTo] = parseUrlForDates();
        setSelectedFromDate(urlFrom);
        setSelectedToDate(urlTo);
    }, [column?.getFilterValue(), parseColumnValue, parseUrlForDates]);

    const setDateValue = (column: Column<IOrderHistoryTableData> | Column<ILxsBillingDocument> | undefined, from: string, to: string) => {
        column?.setFilterValue([from, to]);
    };

    const handleApply = useCallback(() => {
        setDateValue(column, selectedFromDate, selectedToDate);
        updateUrlParams({
            action: UrlParamAction.Set,
            paramName: filter.fromParamName,
            value: selectedFromDate
        });
        updateUrlParams({
            action: UrlParamAction.Set,
            paramName: filter.toParamName,
            value: selectedToDate
        });
    }, [column, selectedFromDate, selectedToDate, filter.fromParamName, filter.toParamName, setDateValue]);

    const canApply = Boolean(selectedFromDate || selectedToDate);

    return (
        <>
            <legend>{filter.legend ?? resources.globalFilterConfirmedDateLegend}</legend>
            <div className={`${CLASS_NAME}__input-group-wrapper`}>
                <div className={`${CLASS_NAME}__input-group-container`}>
                    <label htmlFor={filter.fromParamName ?? 'fromDate'}>{resources.globalFilterFromLabel}</label>
                    <span className={`${CLASS_NAME}__datepicker-toggle-button`}></span>
                    <div className={`${CLASS_NAME}__datepicker-toggle`}>
                        <input
                            id={filter.fromParamName}
                            type='date'
                            className='datepicker-input'
                            onClick={e => {
                                const target = e.currentTarget as HTMLInputElement & { showPicker: () => void };
                                target.showPicker?.();
                            }}
                            value={selectedFromDate && formatDateForInput(selectedFromDate, localizationState)}
                            onChange={e => {
                                setSelectedFromDate(e.target.value);
                            }}
                            max={selectedToDate}
                        />
                    </div>

                    <input
                        type='text'
                        className='datepicker-text-input'
                        value={selectedFromDate ? formatDateForInput(selectedFromDate, localizationState) : ''}
                        placeholder={getDateFormat(localizationState?.dateLocale as DateLocale)}
                        readOnly
                    />
                </div>

                <div className={`${CLASS_NAME}__input-group-container`}>
                    <label htmlFor={filter.toParamName ?? 'toDate'}>{resources.globalFilterToLabel}</label>
                    <span className={`${CLASS_NAME}__datepicker-toggle-button`}></span>
                    <div className={`${CLASS_NAME}__datepicker-toggle`}>
                        <input
                            id={filter.toParamName}
                            type='date'
                            className='datepicker-input'
                            onClick={e => {
                                const target = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
                                target.showPicker?.();
                            }}
                            value={selectedToDate ? formatDateForInput(selectedToDate, localizationState) : ''}
                            onChange={e => setSelectedToDate(e.target.value)}
                            min={selectedFromDate || undefined}
                        />
                    </div>

                    <input
                        type='text'
                        className='datepicker-text-input'
                        value={selectedToDate ? formatDateForInput(selectedToDate, localizationState) : ''}
                        placeholder={getDateFormat(localizationState?.dateLocale as DateLocale)}
                        readOnly
                    />
                </div>

                <fieldset className='msc-form__group'>
                    <button
                        disabled={!canApply}
                        className={`msc-cta__primary ${CLASS_NAME}__global-date-btn`}
                        onClick={handleApply}
                        type='button'
                    >
                        {resources.globalApplyDateFilter}
                    </button>
                </fieldset>
            </div>
        </>
    );
};

const MultiFilter: React.FC<{
    filter: MultiFilterConfig;
    table: Table<ILxsBillingDocument> | Table<IOrderHistoryTableData>;
    CLASS_NAME: string;
    setExpanded?: (arg: object) => void;
}> = ({ filter, table, CLASS_NAME, setExpanded }) => {
    const column = table.getColumn(filter.columnId);
    const uniqueVals = column?.getFacetedUniqueValues?.();
    const derivedKeys: string[] = Array.from(uniqueVals?.keys() ?? []).map(key => String(key));
    const uniqueKeys = filter.explicitKeys && filter.explicitKeys.length > 0 ? filter.explicitKeys : derivedKeys.sort();

    if (uniqueKeys.length === 0) {
        return null;
    }

    const getCurrentValues = (): string[] => {
        const columnValue = column?.getFilterValue();
        if (typeof columnValue === 'string' && columnValue.length > 0) {
            return columnValue
                .split(MULTI_VALUE_SEPARATOR)
                .map(splitValue => splitValue.trim())
                .filter(Boolean);
        }
        if (Array.isArray(columnValue)) {
            return columnValue.map(columnValue => String(columnValue));
        }
        try {
            const url = new URL(window.location.href);
            return url.searchParams
                .getAll(filter.paramName)
                .map(s => s.trim())
                .filter(Boolean);
        } catch {
            return [];
        }
    };

    const setMultiValue = (inputValue: string) => {
        const currentUrl = new URL(window.location.href);
        const status = currentUrl.searchParams.getAll(filter.paramName);
        const textValue = filter.mapKeyToText ? filter.mapKeyToText(inputValue) : inputValue;

        let filterValues = [...(column?.getFilterValue() ? (column?.getFilterValue() as string).split(MULTI_VALUE_SEPARATOR) : [])];
        if (!filterValues.includes(textValue) && !filterValues.includes(inputValue)) {
            filterValues.push(textValue);
            updateUrlParams({
                action: UrlParamAction.Set,
                paramName: filter.paramName,
                value: [...status, inputValue]
            });
        } else {
            filterValues = filterValues.filter((val: string) => val !== textValue && val !== inputValue);
            updateUrlParams({
                action: UrlParamAction.Set,
                paramName: filter.paramName,
                value: status.filter((val: string) => val !== inputValue && val !== textValue)
            });
        }
        column?.setFilterValue(filterValues.sort().join(MULTI_VALUE_SEPARATOR));
    };

    const currentValues = getCurrentValues();

    return (
        <fieldset className={`${CLASS_NAME}__status-filter msc-form__group`}>
            <legend>{filter.legend}</legend>
            {uniqueKeys.map(key => {
                const rawKey = key;
                const convertedKey = filter.mapKeyToText ? filter.mapKeyToText(key) : key;
                const checked = currentValues.includes(key) || currentValues.includes(convertedKey);
                const labelText = filter.mapKeyToText ? filter.mapKeyToText(rawKey) : rawKey;
                return (
                    <div className='msc-form__check' key={rawKey}>
                        <label htmlFor={`${filter.id}-${rawKey}`}>
                            <input
                                checked={checked}
                                type='checkbox'
                                name={labelText}
                                value={rawKey}
                                id={`${filter.id}-${rawKey}`}
                                onChange={e => setMultiValue(rawKey)}
                            />
                            {labelText}
                        </label>
                    </div>
                );
            })}
        </fieldset>
    );
};

const GenericFilterBox: React.FC<GenericFilterBoxProps> = ({
    table,
    resources,
    context,
    localizationState,
    isFilterCollapsed = false,
    filters,
    isCollapsible,
    setIsFilterCollapsed,
    setColumnFilters,
    setExpanded
}) => {
    const CLASS_NAME = 'lxs-filter-box';
    const isMobile = isMobileView(context);

    const hasTableData = table.getRowModel().rows.length > 0;

    const visibleFilters = filters.filter(filter => {
        if (filter.type === 'date') {
            return true;
        }

        if (filter.type === 'multi') {
            const column = table.getColumn(filter.columnId);
            const uniqueVals = column?.getFacetedUniqueValues?.();
            const hasOptions = uniqueVals && uniqueVals.size > 0;
            return hasTableData && hasOptions;
        }

        return true;
    });

    const resetFilters = () => {
        setColumnFilters([]);
        filters.forEach(filter => {
            if (filter.type === 'date') {
                updateUrlParams({ action: UrlParamAction.Delete, paramName: filter.fromParamName });
                updateUrlParams({ action: UrlParamAction.Delete, paramName: filter.toParamName });
            } else if (filter.type === 'multi') {
                updateUrlParams({ action: UrlParamAction.Delete, paramName: filter.paramName });
            }
        });
    };

    return (
        <div className='msc-section-box'>
            <h4
                className={`${CLASS_NAME}__filter-title msc-section-box__title`}
                onClick={() => {
                    if (isMobile && setIsFilterCollapsed) {
                        setIsFilterCollapsed(state => !state);
                    }
                }}
            >
                {resources.globalFilterHeading}
            </h4>
            <div className={`${CLASS_NAME}__filter-divider`} />
            {!isFilterCollapsed && (
                <>
                    <button onClick={resetFilters} className={`${CLASS_NAME}__filter-reset${!isCollapsible ? '-not-collapsible' : ''}`}>
                        {resources.globalResetFilter}
                    </button>
                    {visibleFilters.map((filter, idx) => {
                        const isLast = idx === visibleFilters.length - 1;

                        return (
                            <React.Fragment key={filter.id}>
                                {filter.type === 'date' && (
                                    <DateFilter
                                        filter={filter}
                                        table={table}
                                        resources={resources}
                                        localizationState={localizationState}
                                        CLASS_NAME={CLASS_NAME}
                                        context={context}
                                    />
                                )}
                                {filter.type === 'multi' && (
                                    <MultiFilter filter={filter} table={table} CLASS_NAME={CLASS_NAME} setExpanded={setExpanded} />
                                )}

                                {!isLast && <div className={`${CLASS_NAME}__filter-divider`} />}
                            </React.Fragment>
                        );
                    })}
                </>
            )}
        </div>
    );
};

export default GenericFilterBox;
